import React, { useRef } from 'react';
import { Container, Section } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { ExpertCardListings } from '@fiverr-private/listings';
import { GigCardListings } from '@fiverr-private/gig_listings';
import { getContext } from '@fiverr-private/fiverr_context';
import { FiverrIcon } from '@fiverr-private/visuals';
import { useEnteredView } from '@fiverr-private/hooks';
import { bqEvents, sendBqEvent } from '../../../lib/bq';
import { DisplayMode, InitialProps } from '../../../lib/types';
import { PAGE_NAME, T_PREFIX } from '../../../lib/constants';
import { SaveToListButton } from '../SaveToListButton';
import { COMPONENT_NAME } from './constants';

interface RecommendationsListProps {
    matchmakingList: InitialProps['matchmakingList'];
    currency: InitialProps['currency'];
    displayMode: InitialProps['displayMode'];
    matchmakingNotes: InitialProps['matchmakingNotes'];
    username?: string;
}

export const RecommendationsList = ({
    matchmakingList,
    username,
    displayMode,
    currency,
    matchmakingNotes,
}: RecommendationsListProps) => {
    const { userId } = getContext();

    const isListEmpty = matchmakingList.items.length === 0;

    const observableRef = useRef<HTMLDivElement>(null);

    useEnteredView(observableRef, {
        threshold: 0,
        onEntered: () => {
            sendBqEvent(
                bqEvents.viewed_recommendations_list({
                    elementName: 'recommendations_list',
                    buyerId: userId,
                    listId: matchmakingList?.id,
                    componentName: COMPONENT_NAME,
                })
            );
        },
    });

    return (
        <Section>
            <Container gap="6" direction="column" display="flex">
                <Container display="flex" justifyContent="spaceBetween">
                    <Container>
                        <Text fontWeight="bold" data-testid="title" size="b_lg">
                            {username ? (
                                <I18n k={`${T_PREFIX}.title_with_username`} params={{ username }} />
                            ) : (
                                <I18n k={`${T_PREFIX}.title_without_username`} />
                            )}
                        </Text>
                        <Text size="b_sm" data-testid="subtitle">
                            <I18n k={`${T_PREFIX}.subtitle`} />
                        </Text>
                    </Container>
                    {!isListEmpty && (
                        <SaveToListButton
                            items={matchmakingList.items}
                            displayMode={displayMode}
                            listId={matchmakingList.id}
                        />
                    )}
                </Container>
                {isListEmpty ? (
                    <Text color="bodySecondary" size="b_sm" data-testid="no-results">
                        <I18n k={`${T_PREFIX}.no_results`} />
                    </Text>
                ) : (
                    <Container ref={observableRef}>
                        <Container marginBottom="3" display="flex" justifyContent="spaceBetween">
                            <Text color="bodySecondary" size="b_xs">
                                <I18n
                                    k={`${T_PREFIX}.number_of_${
                                        displayMode === DisplayMode.Sellers ? 'freelancers' : 'services'
                                    }`}
                                    params={{ number: matchmakingList?.items.length }}
                                />
                            </Text>
                            {matchmakingList.operatorName && (
                                <Container display="flex" alignItems="center">
                                    <Text color="bodySecondary" size="b_xs">
                                        <I18n k={`${T_PREFIX}.created_by`} />
                                    </Text>
                                    <Container display="flex" marginLeft="1" alignItems="center">
                                        <FiverrIcon color="business_1200" size="md" />
                                        <Text
                                            color="bodySecondary"
                                            size="b_xs"
                                            marginLeft="1"
                                            data-testid="operator-name"
                                        >
                                            {matchmakingList.operatorName.split(' ')[0]}
                                        </Text>
                                    </Container>
                                </Container>
                            )}
                        </Container>
                        {displayMode === DisplayMode.Sellers ? (
                            <ExpertCardListings
                                forceSmallCard
                                items={matchmakingList.items}
                                layout="GRID"
                                cardVariant="service"
                                currency={currency}
                                notes={matchmakingNotes}
                                onImpression={() => {
                                    sendBqEvent(
                                        bqEvents.viewed_seller_recommendations_list({
                                            elementName: 'seller_recommendations_list',
                                            buyerId: userId,
                                            listId: matchmakingList?.id,
                                            componentName: COMPONENT_NAME,
                                        })
                                    );
                                }}
                                displayOptions={{
                                    showContact: true,
                                    showSaveExpert: true,
                                    mainCta: 'contact',
                                    showJobTitle: false,
                                    showSeeProfile: false,
                                }}
                            />
                        ) : (
                            <GigCardListings
                                onImpression={() => {
                                    sendBqEvent(
                                        bqEvents.viewed_gigs_recommendations_list({
                                            elementName: 'gig_recommendations_list',
                                            buyerId: userId,
                                            listId: matchmakingList?.id,
                                            componentName: COMPONENT_NAME,
                                        })
                                    );
                                }}
                                source={PAGE_NAME}
                                gigs={matchmakingList.items}
                                isExperiential
                                context={{
                                    currency,
                                    sourceComponent: COMPONENT_NAME,
                                }}
                            />
                        )}
                    </Container>
                )}
            </Container>
        </Section>
    );
};
